import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';
import { DatatableColumn, DatatableSorting, FetchData, RktTableComponent } from '@rocketfinancialcorp/rocket-ui/table';
import { FilterMenuComponent, SuccessModalComponent } from '@shared/components';
import { FilterField, FilterValues, FundOnHold, FundsOnHoldList, RequestPageParams } from '@shared/models';
import { FundsOnHoldService } from '@shared/services';
import { ErrorUtils, filterCreatedAtDateRange, filterAmountMinMax } from '@shared/utils';
import { isUndefined } from 'lodash-es';
import { Subject, takeUntil } from 'rxjs';
import { CreateFundOnHoldComponent } from '../create-fund-on-hold/create-fund-on-hold.component';

@Component({
  selector: 'app-funds-on-hold-list',
  imports: [RktTableComponent, RktButtonDirective, RktIconComponent, FilterMenuComponent],
  templateUrl: './funds-on-hold-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundsOnHoldListComponent implements OnDestroy {
  private fundsOnHoldService = inject(FundsOnHoldService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private modalService = inject(ModalService);
  private ref = inject(ChangeDetectorRef);

  financialAccountId = input<string>();

  fundsOnHold: FundOnHold[] = [];

  columns: DatatableColumn[] = [
    { name: 'Created At', prop: 'createdAt', fixedSize: 150, colType: 'date-time', isSortable: true, sortProp: 'createdAt' },
    { name: 'Fund On Hold ID', prop: 'id', fixedSize: 150, colType: 'short-id' },
    { name: 'Memo', prop: 'memo', flexSize: 300 },
    { name: 'Expiry Date', prop: 'expiry', fixedSize: 150, colType: 'date-time' },
    { name: 'Total Amount', prop: 'amount', flexSize: 150, colType: 'amount', colAlign: 'right' },
    { name: 'Status', prop: 'status', fixedSize: 140, colType: 'status-tag', colAlign: 'right' },
  ];

  filters: FilterField[] = [
    {
      name: 'status',
      displayName: 'Status',
      type: 'RADIO',
      options: [
        { label: 'All', value: undefined },
        { label: 'Active', value: 'ACTIVE' },
        { label: 'Released', value: 'RELEASED' },
        { label: 'Expired', value: 'EXPIRED' },
      ],
    },
    {
      name: 'financialAccountId',
      displayName: 'Financial Account ID',
      type: 'INPUT',
    },
    {
      name: 'externalRefId',
      displayName: 'External Reference ID',
      type: 'INPUT',
    },
    {
      name: 'releasedTxnId',
      displayName: 'Released Transaction ID',
      type: 'INPUT',
    },
    {
      name: 'expiry',
      displayName: 'Expiry Date',
      type: 'DATE',
      options: [
        { label: 'Today', value: 'TODAY' },
        { label: 'This week', value: 'THIS_WEEK' },
        { label: 'This month', value: 'THIS_MONTH' },
        { label: 'Custom range', value: 'CUSTOM' },
      ],
    },
    {
      name: 'amount',
      displayName: 'Amount',
      type: 'AMOUNT',
    },
  ];

  sortParams: DatatableSorting = { key: 'createdAt', sortProp: 'createdAt', sortDir: 'desc' };

  totalElements = 0;

  isLoading = false;

  emptyListMessage = 'No Funds on Hold found.';

  activeFilters: FilterValues = {};

  activeFiltersCount = 0;

  get activePage(): number {
    const routePage = this.activatedRoute?.snapshot?.queryParams?.page;
    return routePage ? parseInt(routePage, 10) : 0;
  }

  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getFundsOnHold({ page, size, sortParams }: FetchData): void {
    this.isLoading = true;

    if (sortParams) {
      this.sortParams = sortParams;
    }

    this.updateQueryParams(page);

    const financialAccountId = (this.activeFilters['financialAccountId'] as string) || this.financialAccountId();

    if (!financialAccountId) {
      return;
    }

    const expiryRange = filterCreatedAtDateRange(this.activeFilters['expiry'] as string);
    const { min: amountFromStr, max: amountToStr } = filterAmountMinMax(this.activeFilters['amount'] as string);

    this.fundsOnHoldService
      .getFundsOnHold({
        page,
        size: size || 10,
        financialAccountId,
        sortParams: sortParams ?? this.sortParams,
        status: (this.activeFilters['status'] as string) || undefined,
        externalRefId: (this.activeFilters['externalRefId'] as string) || undefined,
        releasedTxnId: (this.activeFilters['releasedTxnId'] as string) || undefined,
        expiryFrom: expiryRange?.from,
        expiryTo: expiryRange?.to,
        amountFrom: amountFromStr ? Number(amountFromStr) : undefined,
        amountTo: amountToStr ? Number(amountToStr) : undefined,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.emptyListMessage = 'No Funds on Hold found.';
          this.onFetchComplete(response);
        },
        error: (error) => {
          this.emptyListMessage = 'Unable to fetch Funds on Hold.';
          this.onFetchComplete();
          ErrorUtils.catchError('fundsOnHoldService.getFundsOnHold error', error);
        },
      });
  }

  onFetchComplete(fundsOnHoldList?: FundsOnHoldList): void {
    const { content = [], totalElements = 0 } = fundsOnHoldList || {};
    this.fundsOnHold = content;
    this.totalElements = totalElements;
    this.isLoading = false;
    this.ref.markForCheck();
  }

  updateQueryParams(page?: RequestPageParams['page']): void {
    if (!isUndefined(page) && this.activePage !== page) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { page: page > 0 ? page : undefined },
        queryParamsHandling: 'merge',
      });
    }
  }

  onRowClick(fundOnHold: FundOnHold): void {
    if (!fundOnHold.id || !this.financialAccountId()) {
      return;
    }

    this.router.navigate(['../../', this.financialAccountId(), 'funds-on-hold', fundOnHold.id], {
      relativeTo: this.activatedRoute,
    });
  }

  applyFilters(filterValues: FilterValues): void {
    this.activeFilters = filterValues;
    this.activeFiltersCount = Object.values(filterValues).filter(Boolean).length;
    this.getFundsOnHold({ page: 0 });
  }

  createFundOnHold(): void {
    const modalRef = this.modalService.open(CreateFundOnHoldComponent, {
      className: 'entity-form-modal',
    });

    modalRef.componentInstance.financialAccountId = this.financialAccountId()!;

    modalRef.result.then(
      (result) => {
        if (result) {
          const successModalRef = this.modalService.open(SuccessModalComponent, {
            className: 'confirm-modal',
          });
          successModalRef.componentInstance.customTitle = 'Fund On Hold created successfully';

          this.getFundsOnHold({ page: 0 });
        }
      },
      () => false,
    );
  }
}
